// Stills of the characters, for the buttons that choose one.
//
// The panel used to put an emoji on a colour chip for the animal and another emoji on the
// hat, and neither of those is a thing this game draws. In a game whose hitbox IS the
// picture, the only honest thing on a button that picks a character is the character — so
// this renders the real drawGorilla, the same function the match renders with, into a
// canvas and hands back a data URL.
//
// Nothing here touches a match. drawGorilla reads the species and the clothes off the player
// it is handed (lookFor prefers `p.look`), and the pose off that same player's own antic
// fields — a model with none of them stands still, which is what a still is for.

import { withCanvas } from '../canvas.js';
import { state } from '../state.js';
import { drawGorilla } from './gorilla.js';

/**
 * The whole animal, at 1:1.
 *
 * Sized by measuring rather than by guessing: the widest species is the hippo at -35..+38
 * and the tallest is the orangutan, whose head starts 41 above the anchor. The feet reach
 * 29 below it. 78x72 around an anchor at (39, 42) holds every one of the thirteen with
 * room to spare — and holds them all at the same scale, so a gibbon looks like the smaller
 * animal it is instead of every species being stretched to fill its own button.
 */
const BODY = { w: 78, h: 72, ax: 39, ay: 42 };

/**
 * The head and whatever is on it, for the accessory buttons.
 *
 * The tallest thing in the wardrobe is the top hat, 36 above the centre of the head, and
 * the neck pieces reach 20 below it. Cropped to the same window for every accessory rather
 * than to each one's own edges, on purpose: the head is in the picture, so a cap reads as
 * something that goes on top of a head instead of a shape floating in a box.
 */
const HEAD = { w: 54, h: 66, ax: 27, ay: 64 };

/** One still per character, and characters do not change: 23 canvases, once. */
const cache = new Map();

function memo(key, build) {
  if (!cache.has(key)) cache.set(key, build());
  return cache.get(key);
}

function still(look, box) {
  const cv = document.createElement('canvas');
  cv.width = box.w;
  cv.height = box.h;

  const c = cv.getContext('2d');
  // Pixel art at 1:1: nothing is resampled, so nothing is smoothed.
  c.imageSmoothingEnabled = false;

  const model = { x: box.ax, y: box.ay, alive: true, look };

  // Both sides face the same way in the wardrobe. `facingOf` reads the match's own facing,
  // which flips when a gorilla turns to throw — a preview that mirrored itself because of
  // something happening in a match behind the panel would be a preview of the wrong thing.
  const facing = state.facing;
  state.facing = {};

  withCanvas(c, () => drawGorilla(model, 0));

  state.facing = facing;

  return cv.toDataURL('image/png');
}

/** The animal alone: choosing a species is about the animal, not about its hat. */
export function speciesPreview(index) {
  return memo(`s${index}`, () => still({ species: index, worn: {} }, BODY));
}

/** The side's own animal with one accessory on, which is how it will look on the roof. */
export function accessoryPreview(species, accessory) {
  return memo(
    `a${species}:${accessory.id}`,
    () => still({ species, worn: { [accessory.slot]: accessory.id } }, HEAD),
  );
}
